
/**
 * Helper to generate slug for a blog post
 * Uses client-side fallback to avoid RPC issues
 */
export const generateSlug = async (title: string): Promise<string> => {
  if (!title) return '';
  
  // Client-side slug generation function that avoids RPC calls
  const slugify = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
      .replace(/\s+/g, '-')     // Replace spaces with hyphens
      .trim()                   // Trim leading/trailing whitespace
      .replace(/-+/g, '-')      // Replace multiple hyphens with single hyphen
      .substring(0, 60);        // Limit length
  };
  
  try {
    // Generate base slug
    const baseSlug = slugify(title);
    
    // Add a random suffix to ensure uniqueness
    const timestamp = new Date().getTime().toString().slice(-4);
    
    return `${baseSlug}-${timestamp}`;
  } catch (error: unknown) {
    console.error('Error in slug generation:', error);
    return `${slugify(title)}-${new Date().getTime().toString().slice(-4)}`;
  }
};

/**
 * Calculate the estimated reading time for a given content
 * @param content HTML content of the blog post
 * @returns Number of minutes to read
 */
export const calculateReadingTime = (content: string): number => {
  // Strip HTML tags to get plain text
  const text = content.replace(/<[^>]*>?/gm, '');
  
  // Count words (average reading speed is 200-250 words per minute)
  const words = text.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  
  // Return at least 1 minute
  return Math.max(1, minutes);
};

/**
 * Process HTML content to ensure proper paragraph formatting
 * @param content HTML content of the blog post
 * @returns Properly formatted HTML content
 */
export const formatHtmlContent = (content: string): string => {
  if (!content) return '';
  
  // If content doesn't contain any HTML tags, wrap it in paragraphs
  if (!/<[a-z][\s\S]*>/i.test(content)) {
    // Split by double newlines and create paragraphs
    return content
      .split(/\n\s*\n/)
      .map(paragraph => `<p>${paragraph.trim()}</p>`)
      .join('');
  }
  
  return content;
};
